import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ToasterState } from 'src/AppState/ToasterState';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { Resource } from 'src_common/common/mobx-utils/Resource';

const CUSTOMER_ACCOUNT_TYPE = 'customer';
const DEFAULT_MARK_ACTIVE_THRESHOLD_SECONDS = 60;
const SECONDS_PER_MINUTE = 60;
const MIN_MINUTES = 1;
const MAX_MINUTES = 60;

const validateActivityMinutes = (value: string): Result<number> => {
    if (value.trim() === '') {
        return Result.createError('This field is required.');
    }

    if (value.includes(',') || value.includes('.')) {
        return Result.createError('Whole number expected');
    }

    const minutes = parseInt(value, 10);
    if (isNaN(minutes) || String(minutes) !== value.trim()) {
        return Result.createError('Whole number expected');
    }

    if (minutes < MIN_MINUTES || minutes > MAX_MINUTES) {
        return Result.createError(`Value must be between ${MIN_MINUTES} and ${MAX_MINUTES} minutes`);
    }

    return Result.createOk(minutes);
};

const secondsToMinutesValue = (seconds: number): string => {
    return String(seconds / SECONDS_PER_MINUTE);
};

export class ActivityTimeframeState {
    private readonly configResource: Resource<number>;
    private readonly disposers: Array<() => void> = [];

    @observable public isSaving: boolean = false;
    @observable public errorMessage: string | null = null;
    @observable public originalMinutesValue: string = '';

    public readonly activityMinutes: FormInputState<string, number>;

    public constructor(private readonly appState: AppState) {
        this.activityMinutes = FormInputState.new('').map(validateActivityMinutes);

        this.configResource = new Resource(async () => {
            const response = await this.appState.common.trpcClient.client.accountsConfig.getAccountsConfig.query();

            if (response.type === 'error') {
                throw new Error(response.message);
            }

            const customerConfig = response.data.accountTypes[CUSTOMER_ACCOUNT_TYPE];
            const markActiveThreshold = customerConfig?.markActiveThreshold;

            if (typeof markActiveThreshold === 'number' && markActiveThreshold > 0) {
                return markActiveThreshold;
            }

            return DEFAULT_MARK_ACTIVE_THRESHOLD_SECONDS;
        });

        makeObservable(this);

        this.disposers.push(
            reaction(
                () => this.markActiveThresholdSeconds,
                (seconds) => {
                    if (seconds !== null) {
                        this.loadThresholdToForm(seconds);
                    }
                },
                { fireImmediately: true }
            )
        );
    }

    public dispose(): void {
        this.disposers.forEach((dispose) => dispose());
    }

    @computed public get isLoading(): boolean {
        return this.configResource.get().type === 'loading';
    }

    @computed public get markActiveThresholdSeconds(): number | null {
        const result = this.configResource.get();

        if (result.type === 'ready') {
            return result.value;
        }

        if (result.type === 'error') {
            this.errorMessage = result.message;
        }

        return null;
    }

    @action private loadThresholdToForm = (seconds: number): void => {
        const minutesValue = secondsToMinutesValue(seconds);
        this.activityMinutes.setValue(minutesValue);
        this.originalMinutesValue = minutesValue;
    };

    @action private setError = (message: string | null): void => {
        this.errorMessage = message;
    };

    @action private setSaving = (saving: boolean): void => {
        this.isSaving = saving;
    };

    public async refresh(): Promise<void> {
        await this.configResource.refresh();
    }

    @action public updateConfig = async (): Promise<void> => {
        this.activityMinutes.setAsVisited();

        if (this.activityMinutes.result.value.type === 'error') {
            this.setError('Please fix all validation errors');
            return;
        }

        const minutes = this.activityMinutes.result.value.data;
        const thresholdSeconds = minutes * SECONDS_PER_MINUTE;

        try {
            this.setSaving(true);
            this.setError(null);

            const response =
                await this.appState.common.trpcClient.client.accountsConfig.updateAccountTypeMarkActiveThreshold.mutate(
                    {
                        accountType: CUSTOMER_ACCOUNT_TYPE,
                        thresholdSeconds,
                    }
                );

            if (response.type === 'error') {
                this.setError(response.message);
                return;
            }

            ToasterState.get(this.appState.common).setToast({
                variant: 'success',
                title: 'Success',
                message: 'Activity timeframe configuration saved.',
            });

            await this.refresh();
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to update activity timeframe configuration');
        } finally {
            this.setSaving(false);
        }
    };
}
